import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonAccordionGroup, ModalController, NavController } from '@ionic/angular';
import { Chart } from 'chart.js';
import { Objective, ObjectiveProgress, ObjectiveStatus, ObjectiveType, ObjectiveUnit } from 'src/app/models/objective.model';
import { ObjectiveService } from 'src/app/services/objective.service';
import { ProgressModalComponent } from '../progression-modal/progress-modal.component';
import { ObjectiveFormComponent } from '../objective-form/objective-form.component';

@Component({
  selector: 'app-objective-page',
  templateUrl: './objective-page.component.html',
  styleUrls: ['./objective-page.component.scss'],
})
export class ObjectivePageComponent implements OnInit {

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('accordionGroup', { static: true }) accordionGroup!: IonAccordionGroup;

  protected objective: Objective;
  private chart!: any;
  popoverOpen = false;
  popoverEvent: any;
  private chartOptions: any;
  public ObjectiveStatus = ObjectiveStatus
   objectiveUnitLabels = {
      [ObjectiveUnit.KG]: 'Kilogrammes (kg)',
      [ObjectiveUnit.CM]: 'Centimètres (cm)',
      [ObjectiveUnit.KM]: 'Kilomètres (km)',
      [ObjectiveUnit.MIN]: 'Minutes (min)',
      [ObjectiveUnit.REP]: 'Répétitions',
      [ObjectiveUnit.SESSIONS]: 'Sessions',
      [ObjectiveUnit.BPM]: 'Battements par minute (BPM)',
      [ObjectiveUnit.KCAL]: 'Calories (kcal)',
      [ObjectiveUnit.SUBJECTIF]: ' ',
    };


  constructor(
    private objectiveService: ObjectiveService,
    private modalCtrl: ModalController,
    private alertController: AlertController,
    private navCtrl: NavController
  ) { }



  ngOnInit() {
    if (this.objectiveService.selectedObjective) {
      this.objective = this.objectiveService.selectedObjective;
      this.getObjectiveProgression();
    }

  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.createChart();
    }, 200);
  }

  getObjectiveProgression() {
    this.objectiveService.getProgressUpdates(this.objective.id).subscribe((updates: ObjectiveProgress[]) => {
      this.objective.progressUpdates = updates;
    });
  }

  createChart() {
    if (!this.chartCanvas) return;
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    if (this.chart) {
      this.chart.destroy(); // Supprime l'ancien graphique pour éviter les doublons
    }

    const progressData = this.objective.progressUpdates;
    const labels = progressData.map(progress => {
      const date = new Date(progress.updatedAt);
      return date.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    });
    const scores = progressData.map(progress => progress.score);

    let chartType: 'line' | 'bar' = 'line';
    let borderColor = '#4CAF50';
    let backgroundColor = 'rgba(76, 175, 80, 0.2)';

    switch (this.objective.type) {
      case ObjectiveType.Progressif:
        borderColor = '#2196F3'; // Bleu
        backgroundColor = 'rgba(33, 150, 243, 0.2)';
        chartType = 'line';
        break;
      case ObjectiveType.Degressif:
        borderColor = '#F44336'; // Rouge
        backgroundColor = 'rgba(244, 67, 54, 0.2)';
        chartType = 'line';
        break;
      case ObjectiveType.Duree:
      case ObjectiveType.Frequence:
        borderColor = '#FFC107'; // Jaune
        backgroundColor = 'rgba(255, 193, 7, 0.2)';
        chartType = 'bar';
        break;
    }
    this.getChartOptions(this.objective.targetValue, this.objective, scores);
    this.chart = new Chart(ctx, {
      type: chartType,
      data: {
        labels: labels,
        datasets: [{
          label: this.objective.title,
          data: scores,
          borderColor: borderColor,
          backgroundColor: backgroundColor,
          fill: chartType === 'line',
          borderWidth: 2,
          pointRadius: chartType === 'line' ? 5 : 0,
          pointBackgroundColor: borderColor
        }]
      },
      options: this.chartOptions
    });
  }



  getProgressPercentage(): number {
    const total = this.objective.targetValue || 1;
    const completed = this.objective.currentScore || 0;
    return Math.min((completed / total) * 100, 100);
  }

  getStatusText(status: number): string {
    const statusMap = {
      [ObjectiveStatus.EnCours]: "En cours",
      [ObjectiveStatus.Termine]: "Terminé",
      [ObjectiveStatus.Abandonne]: "Abandonné"
    };

    return statusMap[status as ObjectiveStatus] || "Inconnu";
  }


  async openProgressForm() {
    const modal = await this.modalCtrl.create({
      component: ProgressModalComponent,
      componentProps: { objective: this.objective }
    });

    await modal.present();

    const { data, role } = await modal.onDidDismiss();
    if (role === 'success' && data) {
      this.objective.progressUpdates?.push(data);
      await this.objectiveService.getObjectiveById(this.objective.id).subscribe((updatedObjective) => {
        this.objective = updatedObjective;
      });
      this.createChart()
    }
  }


  //PopOver
  openPopover(ev: Event) {
    this.popoverEvent = ev;
    this.popoverOpen = true;
  }

  async deleteObjective() {
    const alert = await this.alertController.create({
      header: 'Confirmation',
      message: 'Voulez-vous vraiment supprimer cet objectif ?',
      buttons: [
        {
          text: 'Annuler',
          role: 'cancel',
          handler: () => {
          }
        },
        {
          text: 'Supprimer',
          handler: () => {
            this.objectiveService.deleteObjective(this.objective.id).subscribe({
              next: () => {
                this.popoverOpen = false;
                this.navCtrl.back()
              },
              error: (err) => {
                console.error('Erreur lors de la suppression de l\'objectif :', err);
              }
            });
          }
        }
      ]
    });

    await alert.present();
  }

  getChartOptions(targetValue: number, objective: Objective, scores: number[]): any {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'category',
          ticks: {
            autoSkip: true,
            maxRotation: 45,
            minRotation: 30,
            font: { size: 10 }
          }
        },
        y: {
          min: 0,
          max: targetValue,
          ticks: { display: true }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true }
      }
    };

    switch (objective.type) {
      case ObjectiveType.Progressif:
        this.chartOptions.scales.y = {
          min: 0,
          max: targetValue,
          ticks: { display: true }
        };
        break;

      case ObjectiveType.Degressif:
        const maxWeight = Math.max(...scores, targetValue);
        const minWeight = Math.min(...scores, targetValue);

        this.chartOptions.scales.y = {
          min: minWeight - 2,
          max: maxWeight + 2,
          ticks: { display: true },
        };
        break;

      case ObjectiveType.Subjectif:
        this.chartOptions.scales.y = {
          min: 0,
          max: 10,
          ticks: { display: false }
        };
        break;

      case ObjectiveType.Frequence:
        this.chartOptions.scales.y = {
          min: 0,
          max: targetValue,
          ticks: { display: true, stepSize: 1 }
        };
        break;

      case ObjectiveType.Duree:
        this.chartOptions.scales.y = {
          min: 0,
          max: targetValue,
          ticks: { display: true },
          title: { display: true, text: "Durée (min)" }
        };
        break;

      default:
        this.chartOptions.scales.y = {
          min: 0,
          max: 10,
          ticks: { display: true }
        };
        break;
    }
  }

  async modalEdit() {
    const modal = await this.modalCtrl.create({
      component: ObjectiveFormComponent,
      componentProps: { objective: this.objective }, // Passer l'objectif à la modale
    });

    modal.onDidDismiss().then(async (result) => {
      if (result.data && result.role === 'confirm') {
        await this.objectiveService.getObjectiveById(this.objective.id).subscribe((updatedObjective) => {
          this.objective = updatedObjective;
        });

      }
    });

    return await modal.present();
  }


}
